// File: /frontend/src/components/user/CategoryShowcase.js
// Immersive category showcase sections for Homepage & Dashboard
// Fetches real banner images from the API at runtime

import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ChevronLeft, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { getBanners } from '../../services/bannerService';

// ─── Section Definitions (no hardcoded images — fetched from API) ─
const SHOWCASE_SECTIONS = [
  // ─── Men Fashion ───────────────────────────────────────────────
  {
    id: 'kurtas',
    title: 'Kurtas & Kurta Sets',
    subtitle: 'Ethnic Elegance',
    description: 'Traditional craftsmanship meets modern style',
    browseUrl: '/user/browse/Men%20Fashion/Ethnic%20Wear/Kurtas%20%26%20Kurta%20Sets',
    category: { l1: 'Men Fashion', l2: 'Ethnic Wear', l3: 'Kurtas & Kurta Sets' },
    theme: { bg: 'from-amber-50 via-orange-50/40 to-yellow-50', accent: 'text-amber-700', button: 'bg-black text-white hover:bg-gray-800' },
  },
  {
    id: 'nehru-jackets',
    title: 'Nehru Jackets',
    subtitle: 'Royal Heritage',
    description: 'Timeless silhouettes for the modern gentleman',
    browseUrl: '/user/browse/Men%20Fashion/Ethnic%20Wear/Nehru%20Jackets',
    category: { l1: 'Men Fashion', l2: 'Ethnic Wear', l3: 'Nehru Jackets' },
    theme: { bg: 'from-stone-100 via-amber-50/30 to-stone-50', accent: 'text-stone-700', button: 'bg-stone-900 text-white hover:bg-stone-800' },
  },
  {
    id: 'shorts',
    title: 'Shorts',
    subtitle: 'Summer Ready',
    description: 'Casual comfort for every occasion',
    browseUrl: '/user/browse/Men%20Fashion/Western%20Wear/Shorts',
    category: { l1: 'Men Fashion', l2: 'Western Wear', l3: 'Shorts' },
    theme: { bg: 'from-sky-50 via-blue-50/30 to-indigo-50/20', accent: 'text-blue-700', button: 'bg-blue-900 text-white hover:bg-blue-800' },
  },
  {
    id: 'sweaters',
    title: 'Sweaters & Sweatshirts',
    subtitle: 'Winter Essentials',
    description: 'Cozy layers for cold-weather style',
    browseUrl: '/user/browse/Men%20Fashion/Winter%20Wear/Sweaters%20%26%20Sweatshirts',
    category: { l1: 'Men Fashion', l2: 'Winter Wear', l3: 'Sweaters & Sweatshirts' },
    theme: { bg: 'from-rose-50/40 via-pink-50/20 to-fuchsia-50/10', accent: 'text-rose-700', button: 'bg-rose-900 text-white hover:bg-rose-800' },
  },
  {
    id: 'jackets',
    title: 'Jackets',
    subtitle: 'Layer Up',
    description: 'Statement outerwear for every season',
    browseUrl: '/user/browse/Men%20Fashion/Winter%20Wear/Jackets',
    category: { l1: 'Men Fashion', l2: 'Winter Wear', l3: 'Jackets' },
    theme: { bg: 'from-gray-100 via-slate-50/40 to-zinc-50', accent: 'text-gray-700', button: 'bg-gray-900 text-white hover:bg-gray-800' },
  },
  {
    id: 'sports-tshirts',
    title: 'Sports T-Shirts',
    subtitle: 'Game On. Gear Up.',
    description: 'Performance wear for every workout',
    browseUrl: '/user/browse/Men%20Fashion/Sportswear/Sports%20T-Shirts',
    category: { l1: 'Men Fashion', l2: 'Sportswear', l3: 'Sports T-Shirts' },
    theme: { bg: 'from-emerald-50 via-teal-50/30 to-green-50/20', accent: 'text-emerald-700', button: 'bg-emerald-800 text-white hover:bg-emerald-700' },
  },
  {
    id: 'night-suits',
    title: 'Night Suits',
    subtitle: 'Comfort Zone',
    description: 'Luxurious sleepwear for restful nights',
    browseUrl: '/user/browse/Men%20Fashion/Sleepwear%20%26%20Loungewear/Night%20Suits',
    category: { l1: 'Men Fashion', l2: 'Sleepwear & Loungewear', l3: 'Night Suits' },
    theme: { bg: 'from-violet-50/40 via-purple-50/20 to-indigo-50/20', accent: 'text-violet-700', button: 'bg-violet-900 text-white hover:bg-violet-800' },
  },
  // ─── Women Fashion ─────────────────────────────────────────────
  {
    id: 'women-sarees',
    title: 'Sarees, Blouses & Petticoats',
    subtitle: 'Timeless Grace',
    description: 'Elegance woven in every thread',
    browseUrl: '/user/browse/Women%20Fashion/Ethnic%20Wear/Sarees%20Blouses%20%26%20Petticoats',
    category: { l1: 'Women Fashion', l2: 'Ethnic Wear', l3: 'Sarees Blouses & Petticoats' },
    theme: { bg: 'from-rose-50 via-pink-50/40 to-fuchsia-50/20', accent: 'text-rose-700', button: 'bg-rose-800 text-white hover:bg-rose-700' },
  },
  {
    id: 'women-kurtis',
    title: 'Kurtis, Sets & Fabrics',
    subtitle: 'Everyday Ethnic',
    description: 'From Anarkali to A-Line — find your fit',
    browseUrl: '/user/browse/Women%20Fashion/Ethnic%20Wear/Kurtis%20Sets%20%26%20Fabrics',
    category: { l1: 'Women Fashion', l2: 'Ethnic Wear', l3: 'Kurtis Sets & Fabrics' },
    theme: { bg: 'from-amber-50 via-orange-50/30 to-yellow-50/20', accent: 'text-amber-700', button: 'bg-amber-800 text-white hover:bg-amber-700' },
  },
  {
    id: 'women-lehengas',
    title: 'Lehengas',
    subtitle: 'Bridal & Party',
    description: 'Statement pieces for unforgettable moments',
    browseUrl: '/user/browse/Women%20Fashion/Ethnic%20Wear/Lehengas',
    category: { l1: 'Women Fashion', l2: 'Ethnic Wear', l3: 'Lehengas' },
    theme: { bg: 'from-red-50/40 via-rose-50/20 to-pink-50/10', accent: 'text-red-700', button: 'bg-red-800 text-white hover:bg-red-700' },
  },
  {
    id: 'women-dresses',
    title: 'Dresses',
    subtitle: 'Western Chic',
    description: 'Maxi, Bodycon, A-Line — dress for every mood',
    browseUrl: '/user/browse/Women%20Fashion/Western%20Wear/Dresses',
    category: { l1: 'Women Fashion', l2: 'Western Wear', l3: 'Dresses' },
    theme: { bg: 'from-violet-50/40 via-purple-50/20 to-indigo-50/10', accent: 'text-violet-700', button: 'bg-violet-800 text-white hover:bg-violet-700' },
  },
  {
    id: 'women-tops',
    title: 'Tops & T-Shirts',
    subtitle: 'Casual Essentials',
    description: 'Crop tops, tank tops, tunics & more',
    browseUrl: '/user/browse/Women%20Fashion/Western%20Wear/Tops%20%26%20T-Shirts',
    category: { l1: 'Women Fashion', l2: 'Western Wear', l3: 'Tops & T-Shirts' },
    theme: { bg: 'from-sky-50 via-blue-50/30 to-indigo-50/20', accent: 'text-sky-700', button: 'bg-sky-800 text-white hover:bg-sky-700' },
  },
  {
    id: 'women-jeans',
    title: 'Jeans & Jeggings',
    subtitle: 'Denim Edit',
    description: 'Skinny, straight, boyfriend — your perfect pair',
    browseUrl: '/user/browse/Women%20Fashion/Western%20Wear/Jeans%20%26%20Jeggings',
    category: { l1: 'Women Fashion', l2: 'Western Wear', l3: 'Jeans & Jeggings' },
    theme: { bg: 'from-blue-50 via-slate-50/30 to-gray-50/20', accent: 'text-blue-700', button: 'bg-blue-800 text-white hover:bg-blue-700' },
  },
  {
    id: 'women-suits',
    title: 'Suits & Dress Material',
    subtitle: 'Festive Elegance',
    description: 'Salwar suits, churidars & dress material',
    browseUrl: '/user/browse/Women%20Fashion/Ethnic%20Wear/Suits%20%26%20Dress%20Material',
    category: { l1: 'Women Fashion', l2: 'Ethnic Wear', l3: 'Suits & Dress Material' },
    theme: { bg: 'from-emerald-50/40 via-teal-50/20 to-green-50/10', accent: 'text-emerald-700', button: 'bg-emerald-800 text-white hover:bg-emerald-700' },
  },
  // ─── Kids Fashion — Boys ───────────────────────────────────────
  {
    id: 'kids-tshirts',
    title: 'Kids T-Shirts & Polos',
    subtitle: 'Little Trendsetters',
    description: 'Fun, colorful tees for boys',
    browseUrl: '/user/browse/Kids%20Fashion/Boys%20Wear/T-Shirts%20%26%20Polos',
    category: { l1: 'Kids Fashion', l2: 'Boys Wear', l3: 'T-Shirts & Polos' },
    theme: { bg: 'from-orange-50 via-yellow-50/30 to-amber-50/20', accent: 'text-orange-600', button: 'bg-orange-600 text-white hover:bg-orange-500' },
  },
  {
    id: 'kids-boys-shirts',
    title: 'Boys Shirts',
    subtitle: 'Smart & Stylish',
    description: 'Casual to party-ready shirts for boys',
    browseUrl: '/user/browse/Kids%20Fashion/Boys%20Wear/Shirts',
    category: { l1: 'Kids Fashion', l2: 'Boys Wear', l3: 'Shirts' },
    theme: { bg: 'from-cyan-50 via-sky-50/30 to-blue-50/20', accent: 'text-cyan-700', button: 'bg-cyan-700 text-white hover:bg-cyan-600' },
  },
  {
    id: 'kids-boys-jeans',
    title: 'Boys Jeans & Pants',
    subtitle: 'Play All Day',
    description: 'Durable bottoms for active kids',
    browseUrl: '/user/browse/Kids%20Fashion/Boys%20Wear/Jeans%20%26%20Pants',
    category: { l1: 'Kids Fashion', l2: 'Boys Wear', l3: 'Jeans & Pants' },
    theme: { bg: 'from-indigo-50/40 via-blue-50/20 to-slate-50/20', accent: 'text-indigo-600', button: 'bg-indigo-700 text-white hover:bg-indigo-600' },
  },
  {
    id: 'kids-boys-ethnic',
    title: 'Boys Ethnic Wear',
    subtitle: 'Festival Ready',
    description: 'Traditional outfits for little princes',
    browseUrl: '/user/browse/Kids%20Fashion/Boys%20Wear/Ethnic%20Wear',
    category: { l1: 'Kids Fashion', l2: 'Boys Wear', l3: 'Ethnic Wear' },
    theme: { bg: 'from-amber-50/50 via-yellow-50/20 to-orange-50/10', accent: 'text-amber-700', button: 'bg-amber-700 text-white hover:bg-amber-600' },
  },
  {
    id: 'kids-boys-sets',
    title: 'Boys Sets & Outfits',
    subtitle: 'Ready-to-Wear',
    description: 'Complete looks in one pick',
    browseUrl: '/user/browse/Kids%20Fashion/Boys%20Wear/Sets%20%26%20Outfits',
    category: { l1: 'Kids Fashion', l2: 'Boys Wear', l3: 'Sets & Outfits' },
    theme: { bg: 'from-teal-50/40 via-emerald-50/20 to-green-50/10', accent: 'text-teal-700', button: 'bg-teal-700 text-white hover:bg-teal-600' },
  },
  // ─── Kids Fashion — Girls ──────────────────────────────────────
  {
    id: 'kids-girls-dresses',
    title: 'Girls Dresses & Frocks',
    subtitle: 'Princess Collection',
    description: 'Beautiful dresses for every occasion',
    browseUrl: '/user/browse/Kids%20Fashion/Girls%20Wear/Dresses%20%26%20Frocks',
    category: { l1: 'Kids Fashion', l2: 'Girls Wear', l3: 'Dresses & Frocks' },
    theme: { bg: 'from-pink-50 via-rose-50/30 to-fuchsia-50/20', accent: 'text-pink-600', button: 'bg-pink-600 text-white hover:bg-pink-500' },
  },
  {
    id: 'kids-girls-tops',
    title: 'Girls Tops & T-Shirts',
    subtitle: 'Cute & Comfy',
    description: 'Adorable tops for little fashionistas',
    browseUrl: '/user/browse/Kids%20Fashion/Girls%20Wear/Tops%20%26%20T-Shirts',
    category: { l1: 'Kids Fashion', l2: 'Girls Wear', l3: 'Tops & T-Shirts' },
    theme: { bg: 'from-fuchsia-50/40 via-pink-50/20 to-rose-50/10', accent: 'text-fuchsia-600', button: 'bg-fuchsia-600 text-white hover:bg-fuchsia-500' },
  },
  {
    id: 'kids-girls-ethnic',
    title: 'Girls Lehengas & Ethnic',
    subtitle: 'Little Diva',
    description: 'Traditional wear for festive celebrations',
    browseUrl: '/user/browse/Kids%20Fashion/Girls%20Wear/Lehengas%20%26%20Ethnic',
    category: { l1: 'Kids Fashion', l2: 'Girls Wear', l3: 'Lehengas & Ethnic' },
    theme: { bg: 'from-red-50/40 via-rose-50/20 to-pink-50/10', accent: 'text-red-600', button: 'bg-red-700 text-white hover:bg-red-600' },
  },
  {
    id: 'kids-girls-jeans',
    title: 'Girls Jeans & Pants',
    subtitle: 'Everyday Comfort',
    description: 'Trendy bottoms for active girls',
    browseUrl: '/user/browse/Kids%20Fashion/Girls%20Wear/Jeans%20%26%20Pants',
    category: { l1: 'Kids Fashion', l2: 'Girls Wear', l3: 'Jeans & Pants' },
    theme: { bg: 'from-sky-50/40 via-blue-50/20 to-indigo-50/10', accent: 'text-sky-600', button: 'bg-sky-700 text-white hover:bg-sky-600' },
  },
  {
    id: 'kids-girls-skirts',
    title: 'Girls Skirts',
    subtitle: 'Twirl & Shine',
    description: 'Playful skirts for every mood',
    browseUrl: '/user/browse/Kids%20Fashion/Girls%20Wear/Skirts',
    category: { l1: 'Kids Fashion', l2: 'Girls Wear', l3: 'Skirts' },
    theme: { bg: 'from-purple-50/40 via-violet-50/20 to-fuchsia-50/10', accent: 'text-purple-600', button: 'bg-purple-600 text-white hover:bg-purple-500' },
  },
  // ─── Kids Fashion — Infant ─────────────────────────────────────
  {
    id: 'kids-infant-rompers',
    title: 'Infant Rompers & Bodysuits',
    subtitle: 'Tiny Bundles',
    description: 'Soft & snuggly wear for babies',
    browseUrl: '/user/browse/Kids%20Fashion/Infant%20Wear/Rompers%20%26%20Bodysuits',
    category: { l1: 'Kids Fashion', l2: 'Infant Wear', l3: 'Rompers & Bodysuits' },
    theme: { bg: 'from-lime-50/40 via-green-50/20 to-emerald-50/10', accent: 'text-lime-700', button: 'bg-lime-700 text-white hover:bg-lime-600' },
  },
  {
    id: 'kids-infant-sets',
    title: 'Infant Sets',
    subtitle: 'Bundle of Joy',
    description: 'Matching sets for your little one',
    browseUrl: '/user/browse/Kids%20Fashion/Infant%20Wear/Sets',
    category: { l1: 'Kids Fashion', l2: 'Infant Wear', l3: 'Sets' },
    theme: { bg: 'from-yellow-50/40 via-amber-50/20 to-orange-50/10', accent: 'text-yellow-700', button: 'bg-yellow-600 text-white hover:bg-yellow-500' },
  },
  {
    id: 'kids-infant-essentials',
    title: 'Infant Essentials',
    subtitle: 'Baby Must-Haves',
    description: 'Bibs, mittens, caps & booties',
    browseUrl: '/user/browse/Kids%20Fashion/Infant%20Wear/Essentials',
    category: { l1: 'Kids Fashion', l2: 'Infant Wear', l3: 'Essentials' },
    theme: { bg: 'from-rose-50/40 via-pink-50/20 to-red-50/10', accent: 'text-rose-600', button: 'bg-rose-600 text-white hover:bg-rose-500' },
  },
  // ─── Kids Fashion — School ─────────────────────────────────────
  {
    id: 'kids-school-shirts',
    title: 'School Shirts & Blouses',
    subtitle: 'School Ready',
    description: 'Neat uniforms for school days',
    browseUrl: '/user/browse/Kids%20Fashion/School%20Uniforms/Shirts%20%26%20Blouses',
    category: { l1: 'Kids Fashion', l2: 'School Uniforms', l3: 'Shirts & Blouses' },
    theme: { bg: 'from-blue-50/40 via-sky-50/20 to-cyan-50/10', accent: 'text-blue-700', button: 'bg-blue-700 text-white hover:bg-blue-600' },
  },
  {
    id: 'kids-school-pants',
    title: 'School Pants & Skirts',
    subtitle: 'Uniform Essentials',
    description: 'Comfortable school bottoms',
    browseUrl: '/user/browse/Kids%20Fashion/School%20Uniforms/Pants%20%26%20Skirts',
    category: { l1: 'Kids Fashion', l2: 'School Uniforms', l3: 'Pants & Skirts' },
    theme: { bg: 'from-slate-100/40 via-gray-50/20 to-zinc-50/10', accent: 'text-slate-700', button: 'bg-slate-700 text-white hover:bg-slate-600' },
  },
  {
    id: 'kids-school-accessories',
    title: 'School Accessories',
    subtitle: 'Finishing Touches',
    description: 'Ties, belts & socks for school',
    browseUrl: '/user/browse/Kids%20Fashion/School%20Uniforms/Accessories',
    category: { l1: 'Kids Fashion', l2: 'School Uniforms', l3: 'Accessories' },
    theme: { bg: 'from-neutral-100/40 via-stone-50/20 to-gray-50/10', accent: 'text-neutral-700', button: 'bg-neutral-700 text-white hover:bg-neutral-600' },
  },
];

// ─── Single Category Card ────────────────────────────────────────
const CategoryCard = ({ item, browseUrl, index }) => {
  const searchUrl = `${browseUrl}?subcategory=${encodeURIComponent(item.name)}`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.4, delay: index * 0.06 }}
    >
      <Link
        to={searchUrl}
        className="group block flex-shrink-0 w-[160px] sm:w-[190px] md:w-[210px]"
      >
        <div className="relative overflow-hidden rounded-2xl bg-gray-100 aspect-[3/4] shadow-sm group-hover:shadow-xl transition-all duration-500">
          <img
            src={item.image}
            alt={item.name}
            className="w-full h-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-110"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
            <span className="text-white text-[11px] font-semibold uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center gap-1">
              View Products <ArrowRight className="w-3 h-3" />
            </span>
          </div>
        </div>
        <div className="mt-2.5 px-0.5">
          <h4 className="text-[13px] sm:text-sm font-semibold text-gray-900 group-hover:text-black transition-colors leading-tight">
            {item.name}
          </h4>
          <p className="text-[10px] sm:text-[11px] text-gray-400 font-medium uppercase tracking-wider mt-0.5">
            View Products
          </p>
        </div>
      </Link>
    </motion.div>
  );
};

// ─── Single Showcase Section ─────────────────────────────────────
const ShowcaseSection = ({ section }) => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    return () => el.removeEventListener('scroll', updateScrollState);
  }, []);

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 240, behavior: 'smooth' });
  };

  return (
    <section className={`relative py-8 sm:py-10 bg-gradient-to-br ${section.theme.bg} -mx-5 sm:-mx-6 lg:-mx-8 px-5 sm:px-6 lg:px-8`}>
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex items-end justify-between">
          <div>
            <span className={`inline-block text-[10px] font-bold uppercase tracking-[0.2em] ${section.theme.accent} mb-1.5`}>
              {section.subtitle}
            </span>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight leading-tight">
              {section.title}
            </h2>
            <p className="text-xs sm:text-sm text-gray-500 mt-0.5">{section.description}</p>
          </div>
          <Link
            to={section.browseUrl}
            className={`hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all duration-300 ${section.theme.button} shadow-sm hover:shadow-md`}
          >
            View All <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>

      {/* Scrollable Cards */}
      <div className="relative max-w-7xl mx-auto">
        {canScrollLeft && (
          <button
            onClick={() => scroll(-1)}
            className="absolute -left-2 sm:left-0 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white/90 backdrop-blur shadow-lg flex items-center justify-center hover:bg-white transition-all"
          >
            <ChevronLeft className="w-4 h-4 text-gray-700" />
          </button>
        )}
        {canScrollRight && (
          <button
            onClick={() => scroll(1)}
            className="absolute -right-2 sm:right-0 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white/90 backdrop-blur shadow-lg flex items-center justify-center hover:bg-white transition-all"
          >
            <ChevronRight className="w-4 h-4 text-gray-700" />
          </button>
        )}

        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto scrollbar-hide pb-2 px-1 snap-x snap-mandatory"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {section.items.map((item, i) => (
            <CategoryCard
              key={item.name}
              item={item}
              browseUrl={section.browseUrl}
              index={i}
            />
          ))}
        </div>
      </div>

      {/* Mobile View All Button */}
      <div className="sm:hidden max-w-7xl mx-auto mt-5">
        <Link
          to={section.browseUrl}
          className={`flex items-center justify-center gap-2 w-full py-3 rounded-full text-xs font-bold uppercase tracking-wider transition-all duration-300 ${section.theme.button}`}
        >
          View All in {section.title} <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </section>
  );
};

// ─── Main Export: Fetches banners from API ────────────────────────
const CategoryShowcase = ({ sections, selectedLevel1 }) => {
  const [populatedSections, setPopulatedSections] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAllBanners = useCallback(async () => {
    try {
      const response = await getBanners({ level: 4 });
      if (!response.success || !response.data) {
        setLoading(false);
        return;
      }

      const banners = response.data;

      // Build populated sections by matching banners to section categories
      const result = [];
      let sectionsToUse = sections
        ? SHOWCASE_SECTIONS.filter(s => sections.includes(s.id))
        : SHOWCASE_SECTIONS;

      // Filter by selected gender if provided
      if (selectedLevel1) {
        sectionsToUse = sectionsToUse.filter(s => s.category.l1 === selectedLevel1);
      }

      for (const section of sectionsToUse) {
        const { l1, l2, l3 } = section.category;
        // Find all banners matching this section's L3 category
        const matched = banners.filter(
          b => b.categoryLevel1 === l1 && b.categoryLevel2 === l2 && b.categoryLevel3 === l3 && b.imageUrl
        );
        if (matched.length > 0) {
          result.push({
            ...section,
            items: matched.map(b => ({
              name: b.categoryLevel4 || b.title || 'View',
              image: b.imageUrl,
            })),
          });
        }
      }

      setPopulatedSections(result);
    } catch (error) {
      console.error('Error fetching showcase banners:', error);
    } finally {
      setLoading(false);
    }
  }, [sections, selectedLevel1]);

  useEffect(() => {
    fetchAllBanners();
  }, [fetchAllBanners]);

  if (loading || populatedSections.length === 0) return null;

  return (
    <div className="space-y-1">
      {populatedSections.map(section => (
        <ShowcaseSection key={section.id} section={section} />
      ))}
    </div>
  );
};

export { SHOWCASE_SECTIONS };
export default CategoryShowcase;
